import { Component } from '@theme/component';
import { formatMoney } from '@theme/money-formatting';
import { CartErrorEvent, CartLinesUpdateEvent, StandardEvents } from '@shopify/events';

const SUCCESS_MESSAGE_DURATION = 5000;

/**
 * Lets a shopper build a bundle from the current product plus its combination products,
 * then adds every selected variant to the cart in a single request.
 *
 * Every ref is optional: the merchant composes the section from blocks and may
 * leave the total or the button out entirely.
 *
 * @typedef {object} ProductCombinationsRefs
 * @property {HTMLElement} [total] - The running total.
 * @property {HTMLElement} [compare] - The struck-through compare-at total.
 * @property {HTMLElement} [prices] - The wrapper that carries the on-sale state.
 * @property {HTMLElement} [message] - The status message region.
 * @property {HTMLButtonElement} [addButton] - The add to cart button.
 * @property {HTMLElement} [defaultLabel] - The idle button label.
 * @property {HTMLElement} [loadingLabel] - The in-flight button label.
 *
 * @extends {Component<ProductCombinationsRefs>}
 */
class ProductCombinationsComponent extends Component {
  /** @type {Element | null} */
  #section = null;

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #messageTimeout;

  #loading = false;

  connectedCallback() {
    super.connectedCallback();

    this.#section = this.closest('.shopify-section, dialog');
    this.#section?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect);

    this.#render();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#section?.removeEventListener(StandardEvents.productSelect, this.#handleProductSelect);
    clearTimeout(this.#messageTimeout);
  }

  /**
   * The selected variants. Deduplicated by variant id because the same card is
   * mirrored into a second list when desktop and mobile layouts cannot share markup.
   *
   * @returns {{ variantId: string, price: number, comparePrice: number }[]}
   */
  get #selection() {
    const items = [];
    const seen = new Set();

    for (const card of this.querySelectorAll('[data-combination-card]')) {
      if (!(card instanceof HTMLElement)) continue;

      const { variantId, available, selected } = card.dataset;
      if (!variantId || seen.has(variantId) || available !== 'true' || selected !== 'true') continue;

      seen.add(variantId);
      items.push({
        variantId,
        price: Number(card.dataset.price),
        comparePrice: Number(card.dataset.comparePrice),
      });
    }

    return items;
  }

  /**
   * Toggles a product in or out of the combination.
   *
   * @param {PointerEvent} event - The click on the card's toggle.
   */
  handleToggle(event) {
    if (!(event.target instanceof Element)) return;

    const card = event.target.closest('[data-combination-card]');
    if (!(card instanceof HTMLElement) || card.dataset.available !== 'true') return;

    const selected = card.dataset.selected !== 'true';
    const { addedLabel, removedLabel } = this.dataset;

    for (const match of this.querySelectorAll(`[data-combination-card][data-variant-id="${card.dataset.variantId}"]`)) {
      if (!(match instanceof HTMLElement)) continue;

      match.dataset.selected = String(selected);
      match.classList.toggle('is-selected', selected);
      match.querySelector('[data-combination-toggle]')?.setAttribute('aria-pressed', String(selected));

      const meta = match.querySelector('[data-combination-meta]');
      if (meta) meta.textContent = (selected ? addedLabel : removedLabel) ?? '';
    }

    this.#setMessage('');
    this.#render();
  }

  /** Adds every selected variant to the cart in one request. */
  async handleAdd() {
    if (this.#loading) return;

    const selection = this.#selection;

    if (!selection.length) {
      this.#setMessage(this.dataset.messageEmpty ?? '', 'error');
      return;
    }

    const deferred = CartLinesUpdateEvent.createPromise();

    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: selection.map(({ variantId }) => ({ merchandiseId: variantId, quantity: 1 })),
        promise: deferred.promise,
      })
    );

    this.#setLoading(true);
    this.#setMessage('');

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: selection.map(({ variantId }) => ({ id: Number(variantId), quantity: 1 })),
          sections: this.#cartSectionIds,
        }),
      });

      const data = await response.json();
      const didError = Boolean(data.status);

      if (didError) {
        this.dispatchEvent(
          new CartErrorEvent({
            error: data.message || 'Add to cart failed',
            code: 'INVALID',
            detail: { description: data.description, errors: data.errors },
          })
        );
      }

      const cart = await this.#refreshCart();

      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          source: 'product-combinations',
          sourceId: this.id,
          itemCount: selection.length,
          productId: this.dataset.productId,
          sections: data.sections,
          didError,
        },
      });

      if (didError) {
        this.#setMessage(data.description || data.message || this.dataset.messageError || '', 'error');
      } else {
        this.#setMessage(this.dataset.messageSuccess ?? '', 'success', SUCCESS_MESSAGE_DURATION);
      }
    } catch (error) {
      deferred.reject(error);

      this.dispatchEvent(
        new CartErrorEvent({
          error: error instanceof Error ? error.message : 'Network error during add to cart',
          code: 'SERVICE_UNAVAILABLE',
        })
      );

      this.#setMessage(this.dataset.messageError ?? '', 'error');
    } finally {
      this.#setLoading(false);
      this.#render();
    }
  }

  /**
   * Keeps the current product's card in sync with the variant picker.
   *
   * @param {import('@shopify/events').ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    // Variant changes inside a product card belong to that card, not to the main product.
    if (event.target instanceof Element && event.target.closest('product-card')) return;

    event.promise
      .then(({ detail }) => {
        const card = this.querySelector('[data-current-product]');
        if (!detail || !(card instanceof HTMLElement)) return;

        const { resource, productId, newProduct } = detail;

        if (newProduct) this.dataset.productId = String(newProduct.id);
        if (productId && String(productId) !== this.dataset.productId) return;
        if (!resource) return;

        Object.assign(card.dataset, {
          variantId: String(resource.id),
          price: String(resource.price),
          comparePrice: String(Math.max(resource.compare_at_price || 0, resource.price)),
          available: resource.available ? 'true' : 'false',
          selected: resource.available ? 'true' : 'false',
        });

        card.classList.toggle('is-unavailable', !resource.available);
        this.#render();
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[product-combinations] Event promise rejected:', error);
      });
  };

  #render() {
    const { total, compare, prices, addButton, defaultLabel } = this.refs;
    const items = this.#selection;

    const totalPrice = items.reduce((sum, item) => sum + item.price, 0);
    const comparePrice = items.reduce((sum, item) => sum + item.comparePrice, 0);
    const onSale = comparePrice > totalPrice;
    const format = this.dataset.moneyFormat || '{{amount}}';
    const currency = this.dataset.currencyCode || 'USD';

    if (total) total.textContent = formatMoney(totalPrice, format, currency);
    if (compare) {
      compare.textContent = formatMoney(comparePrice, format, currency);
      compare.classList.toggle('hidden', !onSale);
    }
    prices?.classList.toggle('is-on-sale', onSale);

    if (defaultLabel) {
      const template = (items.length === 1 ? this.dataset.labelOne : this.dataset.labelOther) ?? '';
      defaultLabel.textContent = template.replace(/\d+/, String(items.length));
    }

    if (addButton && !this.#loading) addButton.disabled = items.length === 0;
  }

  /**
   * @param {string} text - The message to announce.
   * @param {'error' | 'success' | ''} [state] - Styling hint for the message.
   * @param {number} [duration] - Clears the message after this many milliseconds.
   */
  #setMessage(text, state = '', duration) {
    const { message } = this.refs;

    clearTimeout(this.#messageTimeout);
    if (!message) return;

    message.textContent = text;
    message.classList.remove('is-error', 'is-success');
    if (state) message.classList.add(`is-${state}`);

    if (duration) {
      this.#messageTimeout = setTimeout(() => this.#setMessage(''), duration);
    }
  }

  /** @param {boolean} loading - Whether a request is in flight. */
  #setLoading(loading) {
    const { addButton, defaultLabel, loadingLabel } = this.refs;

    this.#loading = loading;
    if (addButton) {
      addButton.disabled = loading;
      addButton.setAttribute('aria-busy', String(loading));
    }
    defaultLabel?.classList.toggle('hidden', loading);
    loadingLabel?.classList.toggle('hidden', !loading);
  }

  /** Section ids the cart needs re-rendered alongside the add request. */
  get #cartSectionIds() {
    return [...document.querySelectorAll('cart-items-component')]
      .map((element) => (element instanceof HTMLElement ? element.dataset.sectionId : null))
      .filter(Boolean)
      .join(',');
  }

  async #refreshCart() {
    /** @type {import('@theme/component-cart-items').CartItemsComponent | null} */
    const cartItems = document.querySelector('cart-items-component');

    if (cartItems) {
      await customElements.whenDefined('cart-items-component');
      return cartItems.fetchCartData();
    }

    const response = await fetch(`${Theme.routes.cart_url}.json`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    });

    if (!response.ok) throw new Error(`Failed to fetch cart: ${response.status}`);

    return response.json();
  }
}

if (!customElements.get('product-combinations-component')) {
  customElements.define('product-combinations-component', ProductCombinationsComponent);
}
